import React, { useState, useEffect } from 'react';
import { getTrainingsCMS, deleteTrainingCMS, toggleTrainingStatus, getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import TrainingForm from '../forms/TrainingForm';
import toast from 'react-hot-toast';

const TrainingsModule = () => {
  const [trainings, setTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const columns = ['Image', 'Name', 'Category', 'Duration', 'Seats', 'Status', 'Actions'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [trainingsResponse, categoriesResponse] = await Promise.all([
        getTrainingsCMS(),
        getCategories()
      ]);
      
      // Fetch trainings
      const trainingsData = trainingsResponse.data || trainingsResponse || [];
      setTrainings(trainingsData);
      
      // Fetch and filter categories for training type
      let categoriesArray = [];
      if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        categoriesArray = categoriesResponse.data.filter(cat => cat.type === 'training');
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesArray = categoriesResponse.filter(cat => cat.type === 'training');
      }
      setCategories(categoriesArray);
      
      console.log('Trainings loaded:', trainingsData.length);
      console.log('Training categories loaded:', categoriesArray.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching trainings');
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const response = await getTrainingsCMS();
      const trainingsData = response.data || response || [];
      setTrainings(trainingsData);
      console.log('Trainings loaded:', trainingsData.length);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      toast.error('Error fetching trainings');
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category?.icon || '🎓';
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this training?')) {
      try {
        await deleteTrainingCMS(id);
        toast.success('Training deleted successfully');
        fetchTrainings();
      } catch (error) {
        toast.error('Error deleting training');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleTrainingStatus(id);
      toast.success(`Training ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchTrainings();
    } catch (error) {
      toast.error('Error toggling training status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ color: '#0A192F', fontSize: '1.3rem' }}>Trainings ({trainings.length})</h2>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <i className="fas fa-plus" /> Add New Training
        </button>
      </div>

      {trainings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: '#F7F9FC', borderRadius: '12px' }}>
          <i className="fas fa-graduation-cap" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F' }}>No trainings found. Click "Add New Training" to create one.</p>
          <button 
            onClick={() => { setEditingItem(null); setShowModal(true); }} 
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Create First Training
          </button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trainings.map((training) => (
                <tr key={training._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {training.imageUrl ? (
                      <img src={getFullImageUrl(training.imageUrl)} alt={training.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#F7F9FC', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-image" style={{ color: '#CBD5E1' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{training.name}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ background: '#E3F2FD', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span>{getCategoryIcon(training.category)}</span> {training.category}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>{training.duration || '-'}</td>
                  <td style={{ padding: '1rem' }}>{training.seats || 'Unlimited'}</td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(training._id, training.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: training.isActive ? '#D4EDDA' : '#F8D7DA', color: training.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {training.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(training)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(training._id)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TrainingForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchTrainings} 
        />
      )}
    </div>
  );
};

export default TrainingsModule;